import {
  AfterViewInit,
  Directive,
  ElementRef,
  HostListener,
  InjectionToken,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Renderer2,
  SimpleChanges
} from '@angular/core';
import { FormGroupDirective, NgForm } from '@angular/forms';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ReadOnlyFormHelper } from '../common-services/read-only-form-helper.service';
import { ReadOnlyService } from '../common-services/read-only.service';

export const READ_ONLY_CONTEXT = new InjectionToken<ReadOnlyScopeDirective>('READ_ONLY_CONTEXT');

@Directive({
  selector: '[appReadOnlyScope]',
  exportAs: 'appReadOnlyScope',
  providers: [
    { provide: READ_ONLY_CONTEXT, useExisting: ReadOnlyScopeDirective }
  ]
})
export class ReadOnlyScopeDirective implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input('appReadOnlyScope') scopeConfig: boolean | string | string[] | '' | null = null;

  public isScopeReadOnly = false;
  public scopeChanged$ = new BehaviorSubject<boolean>(false);

  private mutationObserver: MutationObserver | null = null;
  private subscriptions = new Subscription();

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private readOnlyService: ReadOnlyService,
    private formHelper: ReadOnlyFormHelper,
    @Optional() private formGroupDir?: FormGroupDirective,
    @Optional() private ngForm?: NgForm
  ) { }

  ngOnInit(): void {
    this.subscriptions.add(
      this.readOnlyService.isReadOnly$.subscribe(() => {
        this.evaluateScope();
      })
    );
    this.evaluateScope();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['scopeConfig']) {
      this.evaluateScope();
    }
  }

  ngAfterViewInit(): void {
    this.setupMutationObserver();
    if (this.isScopeReadOnly) {
      this.enforceReadOnlyOnTree(this.elementRef.nativeElement);
    }
  }

  ngOnDestroy(): void {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
    this.subscriptions.unsubscribe();
  }

  /**
   * Evaluates whether this container scope is currently read-only.
   */
  public evaluateScope(): void {
    let active = false;

    if (this.scopeConfig === false) {
      active = false;
    } else if (this.scopeConfig === true) {
      active = true;
    } else if (typeof this.scopeConfig === 'string' && this.scopeConfig.trim().length > 0) {
      active = this.readOnlyService.isComponentReadOnly(this.scopeConfig.trim());
    } else if (Array.isArray(this.scopeConfig) && this.scopeConfig.length > 0) {
      active = this.readOnlyService.isComponentReadOnly(this.scopeConfig);
    } else {
      // Default to global read-only
      active = this.readOnlyService.isReadOnly();
    }

    const stateChanged = this.isScopeReadOnly !== active;
    this.isScopeReadOnly = active;

    if (this.isScopeReadOnly) {
      this.renderer.addClass(this.elementRef.nativeElement, 'app-readonly-scope');
      this.enforceReadOnlyOnTree(this.elementRef.nativeElement);
      this.disableAttachedForms();
    } else {
      this.renderer.removeClass(this.elementRef.nativeElement, 'app-readonly-scope');
    }

    if (stateChanged) {
      this.scopeChanged$.next(this.isScopeReadOnly);
    }
  }

  /**
   * Sets up a MutationObserver to continuously watch for dynamically
   * inserted descendants (tabs, accordion expansions, *ngIf/*ngFor elements).
   */
  private setupMutationObserver(): void {
    if (typeof MutationObserver === 'undefined') {
      return;
    }

    this.mutationObserver = new MutationObserver((mutations) => {
      if (!this.isScopeReadOnly) return;

      for (const mutation of mutations) {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              this.enforceReadOnlyOnTree(node as HTMLElement);
            }
          });
        }
      }
    });

    this.mutationObserver.observe(this.elementRef.nativeElement, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Recursively finds and disables all form controls in the provided DOM node or subtree.
   */
  public enforceReadOnlyOnTree(root: HTMLElement): void {
    if (!root || !root.querySelectorAll) return;

    // 1. Process root if it's an input/control
    this.disableSingleElement(root);

    // 2. Query all descendant inputs, textareas, selects
    const inputs = root.querySelectorAll<HTMLElement>('input, textarea, select');
    inputs.forEach((input) => {
      this.disableInputElement(input);
    });

    // 3. Angular Material controls
    const matControls = root.querySelectorAll<HTMLElement>(
      'mat-select, mat-checkbox, mat-radio-button, mat-slide-toggle, mat-slider, mat-datepicker-toggle'
    );
    matControls.forEach((matCtrl) => {
      this.renderer.setAttribute(matCtrl, 'disabled', 'true');
      this.renderer.addClass(matCtrl, 'mat-form-field-disabled');
      this.renderer.addClass(matCtrl, 'app-readonly-field');
      this.renderer.setStyle(matCtrl, 'pointer-events', 'none');
    });

    // 4. Third-party controls (ng-select, etc.)
    const thirdPartyControls = root.querySelectorAll<HTMLElement>('ng-select, angular-ng-autocomplete');
    thirdPartyControls.forEach((ctrl) => {
      this.renderer.setAttribute(ctrl, 'disabled', 'true');
      this.renderer.addClass(ctrl, 'ng-select-disabled');
      this.renderer.setStyle(ctrl, 'pointer-events', 'none');
    });

    // 5. Mutating Action Buttons (keep Close, Cancel, pagination, tabs, filters, search interactive)
    const mutatingButtons = root.querySelectorAll<HTMLElement>(
      'button[type="submit"], .btn-save, .btn-delete, .btn-add, .btn-update, .btn-submit, .btn-confirm, .btn-reactivate'
    );
    mutatingButtons.forEach((btn) => {
      // Do not disable close or cancel buttons
      if (this.isPreservedButton(btn)) return;

      this.renderer.setAttribute(btn, 'disabled', 'true');
      this.renderer.setStyle(btn, 'display', 'none');
    });

    // 6. Disable drag-drop
    const draggables = root.querySelectorAll<HTMLElement>('[cdkDrag], [draggable]');
    draggables.forEach((dragEl) => {
      this.renderer.setAttribute(dragEl, 'draggable', 'false');
    });
  }

  /**
   * Disables an individual input/textarea/select element without making it illegible.
   */
  private disableInputElement(el: HTMLElement): void {
    // Preserve search inputs
    if (this.isPreservedInput(el)) return;

    if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
      el.readOnly = true;
      this.renderer.setAttribute(el, 'readonly', 'true');
      this.renderer.addClass(el, 'app-readonly-field');
    } else if (el instanceof HTMLSelectElement) {
      el.disabled = true;
      this.renderer.setAttribute(el, 'disabled', 'true');
      this.renderer.addClass(el, 'app-readonly-field');
    }
  }

  private disableSingleElement(el: HTMLElement): void {
    if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) {
      this.disableInputElement(el);
    }
  }

  /**
   * Identifies search / filter inputs that must remain interactive.
   */
  private isPreservedInput(el: HTMLElement): boolean {
    const inputType = el.getAttribute('type') || '';
    if (inputType === 'search') return true;

    const className = el.className || '';
    if (typeof className === 'string' && (className.includes('search') || className.includes('filter'))) {
      return true;
    }

    const placeholder = el.getAttribute('placeholder') || '';
    if (placeholder.toLowerCase().includes('search')) {
      return true;
    }

    return false;
  }

  /**
   * Identifies buttons (e.g. Close, Cancel, Tabs, Filter, Export) that must remain interactive.
   */
  private isPreservedButton(btn: HTMLElement): boolean {
    const className = btn.className || '';
    const text = (btn.textContent || '').trim().toLowerCase();

    if (btn.hasAttribute('mat-dialog-close') || btn.hasAttribute('data-dismiss')) {
      return true;
    }

    if (typeof className === 'string') {
      if (
        className.includes('btn-close') ||
        className.includes('btn-cancel') ||
        className.includes('filter') ||
        className.includes('search') ||
        className.includes('export') ||
        className.includes('download') ||
        className.includes('mat-tab') ||
        className.includes('mat-expansion-panel-header') ||
        className.includes('mat-paginator')
      ) {
        return true;
      }
    }

    if (text === 'close' || text === 'cancel' || text === 'back' || text === 'dismiss') {
      return true;
    }

    return false;
  }

  /**
   * Disables any reactive or template-driven form attached at this container level.
   */
  private disableAttachedForms(): void {
    if (this.formGroupDir && this.formGroupDir.control) {
      this.formHelper.disableForm(this.formGroupDir.control);
    }
    if (this.ngForm && this.ngForm.form) {
      this.formHelper.disableForm(this.ngForm.form);
    }
  }

  /**
   * Trap and prevent Enter key form submissions when read-only.
   */
  @HostListener('keydown.enter', ['$event'])
  onEnterKeyDown(event: KeyboardEvent): void {
    if (!this.isScopeReadOnly) return;

    const target = event.target as HTMLElement;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
      if (!this.isPreservedInput(target)) {
        event.preventDefault();
        event.stopPropagation();
      }
    }
  }
}
